import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { STR } from "@/data/i18n";
import { useLang } from "@/lib/lang";
import { FadeUp, SectionHeading } from "./Shared";

const EXAMPLES = [
    {
        id: "wowkost",
        category: "kost",
        name: "WowKost",
        img: "/assets/contoh-wowkost.webp",
        url: "https://wowkost-id.preview.emergentagent.com/",
    },
    {
        id: "canggukost",
        category: "kost",
        name: "Casa de Julia Canggu",
        img: "/assets/contoh-canggukost.webp",
        url: "https://canggu-kost.preview.emergentagent.com/",
    },
    {
        id: "ngopideponk",
        category: "warkop",
        name: "Warkop Netizen Indonesia",
        img: "/assets/contoh-ngopideponk.webp",
        url: "https://ngopi-deponk.preview.emergentagent.com/",
    },
    {
        id: "kostbatan3",
        category: "kost",
        name: "Kost Batan 3",
        img: "/assets/contoh-kostbatan3.webp",
        url: "https://kost-batan3.preview.emergentagent.com/",
    },
];

export const Showcase = () => {
    const [filter, setFilter] = useState("semua");
    const { lang } = useLang();
    const t = STR.showcase[lang];
    const shown = filter === "semua" ? EXAMPLES : EXAMPLES.filter((e) => e.category === filter);

    return (
        <section id="contoh" data-testid="showcase-section" className="relative py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-5 md:px-8">
                <SectionHeading eyebrow={t.eyebrow} title={t.title} subtitle={t.subtitle} />

                <FadeUp className="mb-10 flex flex-wrap items-center justify-center gap-3">
                    {t.filters.map((f) => (
                        <button
                            key={f.id}
                            type="button"
                            data-testid={`showcase-filter-${f.id}`}
                            onClick={() => setFilter(f.id)}
                            className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-[border-color,background-color,color] duration-200 ${
                                filter === f.id
                                    ? "btn-flame text-white shadow-lg shadow-orange-900/40"
                                    : "border border-white/15 bg-white/5 text-zinc-300 hover:border-amber-400/50 hover:text-amber-300"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </FadeUp>

                {shown.length === 0 ? (
                    <FadeUp>
                        <div
                            data-testid="showcase-empty"
                            className="mx-auto flex max-w-md flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/15 bg-white/[0.02] px-8 py-16 text-center"
                        >
                            <p className="text-sm font-medium text-zinc-400">{t.empty}</p>
                            <p className="mt-2 text-xs text-zinc-600">{t.emptySub}</p>
                        </div>
                    </FadeUp>
                ) : (
                    <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-2">
                        {shown.map((ex, i) => (
                            <FadeUp key={ex.id} delay={i * 0.08} className="h-full">
                                <a
                                    href={ex.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`showcase-card-${ex.id}`}
                                    className="card-surface group flex h-full flex-col overflow-hidden p-0 transition-[transform,border-color] duration-300 hover:-translate-y-2 hover:border-amber-400/30"
                                >
                                    <div className="relative overflow-hidden">
                                        <img
                                            src={ex.img}
                                            alt={`Contoh tampilan website ${ex.name}`}
                                            loading="lazy"
                                            width="1366"
                                            height="805"
                                            className="aspect-[1366/805] w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                                    </div>
                                    <div className="flex flex-1 flex-col p-6">
                                        <h3 className="text-lg font-semibold text-slate-50 transition-colors duration-200 group-hover:text-amber-300">
                                            {ex.name}
                                        </h3>
                                        <p className="mt-2 flex-1 text-sm leading-relaxed text-zinc-400">
                                            {t.descs[ex.id]}
                                        </p>
                                        <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-amber-400">
                                            {t.demo}
                                            <ExternalLink className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                                        </span>
                                    </div>
                                </a>
                            </FadeUp>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};
